/**
 * Strip anything executable out of stored rich text before it reaches `v-html`.
 *
 * Exam long-text answers are rendered as HTML whenever they look like HTML. Editor-authored bodies
 * are safe by construction — Quill's clipboard converts them to a Delta and keeps only registered
 * formats. Legacy `textarea` answers never went through that conversion: they are whatever a person
 * typed years ago, so a value like `<img src=x onerror=...>` would run once it is treated as markup.
 *
 * Allowlist, not blocklist. `DOMParser` builds an inert document — it does not run script, load
 * images, or fetch anything — so parsing the untrusted string here is safe. Everything outside the
 * lists below is dropped. Tag names are compared upper-cased: HTML elements report uppercase, but
 * SVG and MathML report lowercase, and a case-sensitive check would miss them.
 */
const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'DIV',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'PRE',
  'S',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'U',
  'UL',
]);

/**
 * Never keep the contents of these — a dropped `<script>` must not leave its source text behind,
 * and unwrapping `<svg>` / `<math>` would expose the foreign-namespace children hiding inside.
 */
const DISCARDED_TAGS = new Set([
  'BASE',
  'EMBED',
  'FORM',
  'IFRAME',
  'INPUT',
  'LINK',
  'MATH',
  'META',
  'NOSCRIPT',
  'OBJECT',
  'SCRIPT',
  'STYLE',
  'SVG',
  'TEMPLATE',
]);

const ALLOWED_ATTRIBUTES = new Set([
  'alt',
  'class',
  'colspan',
  'data-list',
  'height',
  'href',
  'rel',
  'rowspan',
  'src',
  'style',
  'target',
  'title',
  'width',
]);

const URL_ATTRIBUTES = new Set(['href', 'src']);

const SAFE_URL_PATTERN = /^(?:https?:|mailto:|tel:|data:image\/(?:png|jpeg|gif|webp|bmp);base64,|[/#?])/i;

/** Whitespace and control characters, which browsers ignore inside a scheme (`java\tscript:`). */
const URL_NOISE_PATTERN = /[\u0000-\u0020]/g;

/** CSS that can still reach a loader or an old engine. Quill only needs colours, so drop the rest. */
const UNSAFE_STYLE_PATTERN = /(?:url\s*\(|expression\s*\(|javascript:|@import|behavior\s*:)/i;

function isSafeUrl(value: string): boolean {
  return SAFE_URL_PATTERN.test(value.replace(URL_NOISE_PATTERN, ''));
}

function sanitizeElement(element: Element) {
  for (const attribute of [...element.attributes]) {
    const name = attribute.name.toLowerCase();

    // One rule covers every `on*` handler — `onerror`, `onload`, and whatever ships next.
    if (!ALLOWED_ATTRIBUTES.has(name)) {
      element.removeAttribute(attribute.name);
      continue;
    }

    if (URL_ATTRIBUTES.has(name) && !isSafeUrl(attribute.value)) {
      element.removeAttribute(attribute.name);
      continue;
    }

    if (name === 'style' && UNSAFE_STYLE_PATTERN.test(attribute.value)) {
      element.removeAttribute(attribute.name);
    }
  }

  // A link that survives opens in a new tab without handing the opener over.
  if (element.tagName.toUpperCase() === 'A' && element.getAttribute('href')) {
    element.setAttribute('rel', 'noopener noreferrer');
    element.setAttribute('target', '_blank');
  }
}

function sanitizeRichTextHtml(html: unknown): string {
  if (typeof html !== 'string' || !html) return '';

  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  const walker = parsedDocument.createTreeWalker(parsedDocument.body, NodeFilter.SHOW_ELEMENT);
  const disallowedElements: Element[] = [];
  const sourcelessImages: Element[] = [];

  while (walker.nextNode()) {
    const element = walker.currentNode as Element;

    if (ALLOWED_TAGS.has(element.tagName.toUpperCase())) {
      sanitizeElement(element);

      // An image left with no `src` — either it never had one, or the one it had was rejected
      // above — renders as a blank inline box that still takes up room and pushes the text beside
      // it onto another line. Nothing left to show, so drop the element rather than keep a hole.
      if (element.tagName.toUpperCase() === 'IMG' && !element.getAttribute('src')) {
        sourcelessImages.push(element);
      }

      continue;
    }

    disallowedElements.push(element);
  }

  sourcelessImages.forEach((element) => element.remove());

  // Reverse so a nested match is handled before its parent moves out from under it.
  disallowedElements.reverse().forEach((element) => {
    if (DISCARDED_TAGS.has(element.tagName.toUpperCase())) {
      element.remove();
      return;
    }

    element.replaceWith(...element.childNodes);
  });

  return parsedDocument.body.innerHTML;
}

export { sanitizeRichTextHtml };
